// Lighting engine (ARCH §5.2, LIT-1..6). Two 0..15 channels per cell stored in
// sections (ENG-5): sky light (LIT-1 vertical column fill, attenuating by
// lightOpacity) and block light (seeded from lightEmission). Propagation is a
// 6-neighbour BFS decrementing by max(1, lightOpacity) per step (LIT-2); removal
// is the standard two-queue unlight (LIT-3). PURE (ENG-1) — no three/DOM — so the
// same code lights chunks in the gen worker, on the main thread, and in tests.
//
// `lightChunk` is the bulk pass (LIT-4, run at generation): self-contained per
// chunk (out-of-chunk reads as open air), so cross-chunk seams are corrected when
// a neighbour loads and the chunk is re-meshed (E-4). Edits use the incremental
// `propagate`/`unpropagate` helpers on a caller-supplied grid (World implements it
// cross-chunk) so a single relight stays < 2 ms (LIT-4).
import { LIGHT, TICK, WORLD } from '../config';
import { localIndex, STATE_AIR, type Section, type YRange } from './section';
import type { Chunk } from './chunk';

export const SKY_FULL = LIGHT.MAX; // open-sky value (15)

/** 6-neighbour offsets, shared by every BFS. */
const DIRS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];

// ── Day/night (LIT-6) ────────────────────────────────────────────────────────

/** Moon phase (0..7) for a given absolute time; each day advances it by one. */
export function moonPhaseOf(totalTicks: number): number {
  return Math.floor(totalTicks / TICK.DAY_TICKS) % LIGHT.MOON_PHASES;
}

function smoothstep(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}

/**
 * Sky-light scale for a time of day (LIT-6): 1.0 by day, a moonlit floor by
 * night, with smooth `RAMP_TICKS` dawn/dusk ramps. `moonPhase` modulates the
 * night floor ±`MOON_AMPLITUDE`. Applied in the shader uniform, never baked
 * (LIT-5), so dusk needs no remesh. Result is clamped to [floor, DAY] (E-29).
 */
export function daylightFactor(dayTime: number, moonPhase = 0): number {
  const day = ((dayTime % TICK.DAY_TICKS) + TICK.DAY_TICKS) % TICK.DAY_TICKS;
  // Half-cosine moon curve: full at new/full extremes, dimmest at the quarter.
  const moon = Math.cos((moonPhase / LIGHT.MOON_PHASES) * Math.PI * 2) * LIGHT.MOON_AMPLITUDE;
  const floor = LIGHT.NIGHT_FLOOR + moon;
  const duskEnd = LIGHT.DUSK_START + LIGHT.RAMP_TICKS;
  const dawnStart = TICK.DAY_TICKS - LIGHT.RAMP_TICKS;
  let f: number;
  if (day < LIGHT.DUSK_START) f = LIGHT.DAY;
  else if (day < duskEnd) f = LIGHT.DAY + (floor - LIGHT.DAY) * smoothstep((day - LIGHT.DUSK_START) / LIGHT.RAMP_TICKS);
  else if (day < dawnStart) f = floor;
  else f = floor + (LIGHT.DAY - floor) * smoothstep((day - dawnStart) / LIGHT.RAMP_TICKS);
  return Math.max(floor, Math.min(LIGHT.DAY, f));
}

// ── Generic incremental propagation (LIT-2/3) ────────────────────────────────

/** A mutable light grid over world coordinates. `mark` records touched cells so
 *  the caller can remesh affected chunks. Reads outside loaded data return the
 *  open-sky / no-block defaults (handled by the implementation). */
export interface LightGrid {
  get(x: number, y: number, z: number): number;
  set(x: number, y: number, z: number, v: number): void;
  /** lightOpacity of the block at (x,y,z) — 0 for air, 15 for solid. */
  opacity(x: number, y: number, z: number): number;
  mark(x: number, y: number, z: number): void;
}

/** A flat FIFO of packed (x,y,z) triples — avoids per-cell object churn. */
export class CellQueue {
  private readonly data: number[] = [];
  private head = 0;
  push(x: number, y: number, z: number): void {
    this.data.push(x, y, z);
  }
  get length(): number {
    return (this.data.length - this.head) / 3;
  }
  /** Copies the next triple into `out` (length ≥ 3); returns false when empty. */
  shift(out: number[]): boolean {
    if (this.head >= this.data.length) return false;
    out[0] = this.data[this.head]!;
    out[1] = this.data[this.head + 1]!;
    out[2] = this.data[this.head + 2]!;
    this.head += 3;
    return true;
  }
}

/** BFS-spread light from every cell already set in `queue` (LIT-2). Only raises. */
export function propagate(grid: LightGrid, queue: CellQueue): void {
  const get = grid.get, set = grid.set, opacity = grid.opacity, mark = grid.mark;
  const cur = [0, 0, 0];
  while (queue.shift(cur)) {
    const x = cur[0]!, y = cur[1]!, z = cur[2]!;
    const level = get(x, y, z);
    if (level <= 1) continue;
    for (let d = 0; d < 6; d++) {
      const dir = DIRS[d]!;
      const nx = x + dir[0], ny = y + dir[1], nz = z + dir[2];
      const op = opacity(nx, ny, nz);
      const target = level - (op > 1 ? op : 1);
      if (target > get(nx, ny, nz)) {
        set(nx, ny, nz, target);
        mark(nx, ny, nz);
        queue.push(nx, ny, nz);
      }
    }
  }
}

/**
 * Two-queue removal (LIT-3): clears the light emanating from (sx,sy,sz) and
 * returns the re-seed frontier — cells that are still lit by another source and
 * must be re-propagated. Caller sets the cell to its new value (0, a lower sky
 * seed, or a fresh emission) before calling propagate on the returned frontier.
 */
export function unpropagate(grid: LightGrid, sx: number, sy: number, sz: number): CellQueue {
  const frontier = new CellQueue();
  const old = grid.get(sx, sy, sz);
  if (old === 0) return frontier;
  const get = grid.get, set = grid.set, mark = grid.mark;
  set(sx, sy, sz, 0);
  mark(sx, sy, sz);
  const removal = new CellQueue();
  removal.push(sx, sy, sz);
  // Track the level each removed cell HAD, parallel to the removal queue.
  const levels: number[] = [old];
  let li = 0;
  const cur = [0, 0, 0];
  while (removal.shift(cur)) {
    const x = cur[0]!, y = cur[1]!, z = cur[2]!;
    const lvl = levels[li++]!;
    for (let d = 0; d < 6; d++) {
      const dir = DIRS[d]!;
      const nx = x + dir[0], ny = y + dir[1], nz = z + dir[2];
      const nl = get(nx, ny, nz);
      if (nl === 0) continue;
      if (nl < lvl) {
        set(nx, ny, nz, 0);
        mark(nx, ny, nz);
        removal.push(nx, ny, nz);
        levels.push(nl);
      } else {
        // nl >= lvl: an independent source lights this cell — re-propagate from it.
        frontier.push(nx, ny, nz);
      }
    }
  }
  return frontier;
}

// ── Bulk chunk lighting (LIT-1/4) ────────────────────────────────────────────

/**
 * Bulk-light a freshly generated chunk (LIT-1): sky column fill + block-emitter
 * seed, then a chunk-confined 6-neighbour BFS. Out-of-chunk neighbours read as
 * open air (sky 15, block 0); cross-chunk correction happens on neighbour load
 * via remesh (E-4). Idempotent — clears existing section light first.
 */
export function lightChunk(
  chunk: Chunk,
  opacityOf: (state: number) => number,
  emissionOf: (state: number) => number,
): void {
  const sections = chunk.sections;
  const range: YRange = chunk.range;

  const stateAt = (lx: number, y: number, lz: number): number => {
    if (y < range.minY || y > range.maxY) return STATE_AIR;
    if ((lx | lz) & ~15) return STATE_AIR; // outside this chunk → air
    const section = sections[(y >> 4) + range.sectionYOffset];
    return section ? section.getIndex(localIndex(lx, y, lz)) : STATE_AIR;
  };
  const sectionAt = (y: number): Section | null => sections[(y >> 4) + range.sectionYOffset] ?? null;

  const getSky = (lx: number, y: number, lz: number): number => {
    if (y < range.minY) return 0;
    if (y > range.maxY || (lx | lz) & ~15) return SKY_FULL;
    const s = sectionAt(y);
    return s ? s.skyLight[localIndex(lx, y, lz)]! : SKY_FULL;
  };
  const getBlk = (lx: number, y: number, lz: number): number => {
    if (y < range.minY || y > range.maxY || (lx | lz) & ~15) return 0;
    const s = sectionAt(y);
    return s ? s.blockLight[localIndex(lx, y, lz)]! : 0;
  };

  // Clear + seed. Nothing to store into null sections (implicit sky 15 / block 0).
  for (const s of sections) {
    if (s) { s.skyLight.fill(0); s.blockLight.fill(0); }
  }

  const skyQueue = new CellQueue();
  const blkQueue = new CellQueue();

  // Above the highest filled section every column is open sky (null sections read
  // an implicit 15), so the per-column seed starts there instead of scanning the
  // whole MAX_Y→MIN_Y range — the win on a 672-section Earth column (TAL-3).
  let topSection = -1;
  for (let si = sections.length - 1; si >= 0; si--) if (sections[si]) { topSection = si; break; }
  const topY = (topSection - range.sectionYOffset) * WORLD.SECTION + WORLD.SECTION - 1;

  for (let lx = 0; lx < WORLD.SECTION; lx++) {
    for (let lz = 0; lz < WORLD.SECTION; lz++) {
      let level: number = SKY_FULL;
      for (let y = topY; y >= range.minY; y--) {
        const s = sectionAt(y);
        if (!s) continue; // open air: level unchanged, not stored
        const li = localIndex(lx, y, lz);
        const state = s.getIndex(li);
        const op = opacityOf(state);
        if (op > 0) level = Math.max(0, level - op);
        s.skyLight[li] = level;
        skyQueue.push(lx, y, lz);
        const emission = emissionOf(state);
        if (emission > 0) {
          s.blockLight[li] = emission;
          blkQueue.push(lx, y, lz);
        }
      }
    }
  }

  const skyGrid: LightGrid = {
    get: getSky,
    set: (lx, y, lz, v) => { const s = sectionAt(y); if (s) s.skyLight[localIndex(lx, y, lz)] = v; },
    opacity: (lx, y, lz) => opacityOf(stateAt(lx, y, lz)),
    mark: () => {},
  };
  const blkGrid: LightGrid = {
    get: getBlk,
    set: (lx, y, lz, v) => { const s = sectionAt(y); if (s) s.blockLight[localIndex(lx, y, lz)] = v; },
    opacity: (lx, y, lz) => opacityOf(stateAt(lx, y, lz)),
    mark: () => {},
  };
  propagate(skyGrid, skyQueue);
  propagate(blkGrid, blkQueue);
}
